const INTERIM_JOB_LIMIT = 10;
const MANUAL_JOB_LIMIT = 100;
export const NEW_JOB_WINDOW_MS = 24 * 60 * 60 * 1000;
export function humanizeToken(value, language = 'en') {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'on_site' || normalized === 'onsite') return language === 'ar' ? 'من مقر العمل' : 'On-site';
  if (normalized === 'full_time' || normalized === 'fulltime') return language === 'ar' ? 'دوام كامل' : 'Full-time';
  if (normalized === 'part_time' || normalized === 'parttime') return language === 'ar' ? 'دوام جزئي' : 'Part-time';
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function formatSalary(item, language = 'en') {
  if (item.salary) return item.salary;
  const min = item.salaryMin == null || item.salaryMin === '' ? NaN : Number(item.salaryMin);
  const max = item.salaryMax == null || item.salaryMax === '' ? NaN : Number(item.salaryMax);
  if (!Number.isFinite(min) && !Number.isFinite(max)) return '';
  const currency = item.salaryCurrency || 'USD';
  const formatter = new Intl.NumberFormat(language === 'ar' ? 'ar-LB' : 'en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  });
  if (Number.isFinite(min) && Number.isFinite(max)) {
    return min === max ? `${formatter.format(min)}${language === 'ar' ? '/سنة' : '/yr'}` : `${formatter.format(min)} - ${formatter.format(max)}${language === 'ar' ? '/سنة' : '/yr'}`;
  }
  if (Number.isFinite(min)) return `${formatter.format(min)}+${language === 'ar' ? '/سنة' : '/yr'}`;
  return `${language === 'ar' ? 'حتى' : 'Up to'} ${formatter.format(max)}${language === 'ar' ? '/سنة' : '/yr'}`;
}

export function feedItemToCard(item, scoutId = '', language = 'en') {
  // Backend writes the shape defined in scout/publish.py::save_final_results.
  return {
    ...item,
    id: item.jobId,
    scoutId,
    jobOpening: item.title || (language === 'ar' ? 'وظيفة بلا عنوان' : 'Untitled role'),
    companyName: item.companyName || (language === 'ar' ? 'شركة غير معروفة' : 'Unknown company'),
    employerWebsite: item.employerWebsite || '',
    companyDomain: item.companyDomain || '',
    companyLocation: item.location || '',
    workspace: humanizeToken(item.remoteType, language),
    schedule: humanizeToken(item.employmentType, language),
    salary: formatSalary(item, language),
    jobFunction: item.jobFunction || '',
    fitScore: typeof item.fitScore === 'number' ? item.fitScore : null,
    reason: item.reason || '',
    concern: item.concern || '',
    applyUrl: item.applyUrl || '',
    postedAt: item.postedAt || '',
    postedDate: item.postedDate || '',
    datePosted: item.datePosted || '',
    firstSeenAt: item.firstSeenAt || '',
    createdAt: item.createdAt || '',
    generatedAt: item.generatedAt || '',
    updatedAt: item.updatedAt || '',
    rank: item.rank || 999,
    strongFit: typeof item.fitScore === 'number' ? item.fitScore >= 75 : false,
    source: item.source || '',
  };
}

export function parseTimestamp(value) {
  if (!value) return 0;

  if (value instanceof Date) {
    const dateValue = value.getTime();
    return Number.isNaN(dateValue) ? 0 : dateValue;
  }

  if (typeof value === 'object') {
    const seconds = Number(value.seconds ?? value._seconds);
    if (Number.isFinite(seconds)) {
      const nanoseconds = Number(value.nanoseconds ?? value._nanoseconds ?? 0);
      return (seconds * 1000) + (Number.isFinite(nanoseconds) ? Math.floor(nanoseconds / 1000000) : 0);
    }

    const timestampFields = [
      'postedAt',
      'postedDate',
      'datePosted',
      'firstSeenAt',
      'createdAt',
      'generatedAt',
      'updatedAt',
    ];
    for (const field of timestampFields) {
      const parsedField = parseTimestamp(value[field]);
      if (parsedField > 0) return parsedField;
    }
    return 0;
  }

  if (typeof value === 'number') return value < 1000000000000 ? value * 1000 : value;
  const raw = String(value).trim();
  if (!raw) return 0;
  if (/^\d+$/.test(raw)) {
    const numeric = Number(raw);
    return numeric < 1000000000000 ? numeric * 1000 : numeric;
  }
  const parsed = new Date(raw).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function parseJobTimestamp(job) {
  return parseTimestamp(job);
}

export function isNewJob(job) {
  const posted = parseJobTimestamp(job);
  const now = Date.now();
  return posted > 0 && posted <= now && now - posted <= NEW_JOB_WINDOW_MS;
}

export function profileHasCv(profile, scoutId = '') {
  const scoutCv = scoutId ? profile?.jobScouts?.[scoutId]?.cv : null;
  return Boolean(
    profile?.cvMetadata?.storagePath
    || profile?.cvMetadata?.downloadUrl
    || profile?.cv?.Link
    || profile?.cvUrl
    || scoutCv?.storagePath
    || scoutCv?.fileUrl
    || scoutCv?.downloadUrl
  );
}

export function normalizeCategory(value) {
  return String(value || '').trim().toLowerCase();
}

export function postedJobToCard(job, rank, language = 'en') {
  return {
    ...job,
    id: job.id,
    scoutId: '',
    jobOpening: job.jobOpening || job.title || (language === 'ar' ? 'وظيفة بلا عنوان' : 'Untitled role'),
    companyName: job.companyName || (language === 'ar' ? 'شركة غير معروفة' : 'Unknown company'),
    employerWebsite: job.employerWebsite || job.companyWebsite || '',
    companyDomain: job.companyDomain || '',
    companyLocation: job.companyLocation || job.location || '',
    workspace: job.workspace || '',
    schedule: job.schedule || '',
    salary: job.salary || '',
    jobFunction: job.jobFunction || '',
    fitScore: null,
    reason: '',
    concern: '',
    applyUrl: job.applyUrl || job.applicationUrl || '',
    postedAt: job.postedAt || '',
    postedDate: job.postedDate || '',
    datePosted: job.datePosted || '',
    firstSeenAt: job.firstSeenAt || '',
    createdAt: job.createdAt || '',
    generatedAt: job.generatedAt || '',
    updatedAt: job.updatedAt || '',
    rank,
    strongFit: false,
    source: 'jobseeqr-posted',
  };
}

export function normalizePostedJobs(payload) {
  return Object.entries(payload || {})
    .map(([id, value]) => {
      if (!value || typeof value !== 'object') return null;
      return { id, ...value };
    })
    .filter((job) => job?.id)
    .sort((left, right) => parseJobTimestamp(right) - parseJobTimestamp(left));
}

export function normalizeWorkMode(value) {
  const normalized = normalizeCategory(value).replace(/[\s-]+/g, '_');
  if (['onsite', 'on_site', 'office'].includes(normalized)) return 'on_site';
  if (normalized.includes('hybrid')) return 'hybrid';
  if (normalized.includes('remote')) return 'remote';
  return normalized;
}

export function containsSearchTerm(searchable, term) {
  if (term.length > 2) return searchable.includes(term);
  return searchable.split(/[^a-z0-9+#.]+/).filter(Boolean).includes(term);
}

export function buildManualJobs(postedJobs, filters, language = 'en') {
  const queryTerms = normalizeCategory(filters.query).split(/\s+/).filter(Boolean);
  const locationTerms = normalizeCategory(filters.location).split(/\s+/).filter(Boolean);
  const requestedMode = normalizeWorkMode(filters.workMode);

  return postedJobs
    .filter((job) => {
      const title = String(job.jobOpening || job.title || '').trim();
      const company = String(job.companyName || job.company || '').trim();
      const status = String(job.lifecycleStatus || job.status || '').trim().toLowerCase();
      const closesAt = parseTimestamp(
        job.validThrough || job.applicationDeadline || job.expiresAt || job.expirationDate,
      );
      if (!title || !company || job.published === false || job.expired === true) return false;
      if (['closed', 'deleted', 'draft', 'pending_approval', 'unpublished', 'archived'].includes(status)) return false;
      if (closesAt > 0 && closesAt < Date.now()) return false;

      const searchable = normalizeCategory([
        job.jobOpening,
        job.title,
        job.companyName,
        job.company,
        job.jobFunction,
        job.skills,
      ].flat().filter(Boolean).join(' '));
      const location = normalizeCategory(job.companyLocation || job.location);
      const workMode = normalizeWorkMode(
        job.workspace || job.workArrangement || job.remoteType || job.workMode,
      );
      return queryTerms.every((term) => containsSearchTerm(searchable, term))
        && locationTerms.every((term) => location.includes(term))
        && (!requestedMode || requestedMode === 'all' || workMode === requestedMode);
    })
    .slice(0, MANUAL_JOB_LIMIT)
    .map((job, index) => postedJobToCard(job, index + 1, language));
}

export function categoriesForProfile(profile) {
  const categories = [];
  if (profile?.job) categories.push(profile.job);
  (profile?.jobCategories || []).forEach((category) => {
    if (category && !categories.includes(category)) categories.push(category);
  });
  return categories;
}

export function targetTitlesForProfile(profile) {
  const scoutId = profile?.primaryScoutId || '';
  const scoutTitles = scoutId
    ? profile?.jobScouts?.[scoutId]?.goals?.targetTitles || []
    : [];
  const provisionalTitles = String(profile?.targetRole || '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
  return [...new Set([...scoutTitles, ...provisionalTitles].map(normalizeCategory).filter(Boolean))];
}

export function buildInterimJobs(postedJobs, profile, language = 'en') {
  const categories = categoriesForProfile(profile);
  const primaryCategory = normalizeCategory(categories[0]);
  const alternateCategories = new Set(categories.slice(1).map(normalizeCategory).filter(Boolean));
  const targetTitles = targetTitlesForProfile(profile);
  const selected = [];
  const seen = new Set();

  const addJobs = (jobs) => {
    jobs.forEach((job) => {
      if (!job?.id || seen.has(job.id) || selected.length >= INTERIM_JOB_LIMIT) return;
      seen.add(job.id);
      selected.push(job);
    });
  };

  addJobs(postedJobs.filter((job) => {
    const jobTitle = normalizeCategory(job.jobOpening || job.title);
    return targetTitles.some((targetTitle) => (
      jobTitle.includes(targetTitle) || targetTitle.includes(jobTitle)
    ));
  }));

  // A confirmed title is safer than a broad public category. If the public
  // inventory contains only two matching titles, show two rather than padding
  // the temporary list with unrelated roles from the same department.
  if (targetTitles.length > 0) {
    return selected.slice(0, INTERIM_JOB_LIMIT).map((job, index) => (
      postedJobToCard(job, index + 1, language)
    ));
  }

  if (primaryCategory) {
    addJobs(postedJobs.filter((job) => normalizeCategory(job.jobFunction).includes(primaryCategory)));
  }

  if (alternateCategories.size > 0) {
    addJobs(postedJobs.filter((job) => {
      const jobFunction = normalizeCategory(job.jobFunction);
      return Array.from(alternateCategories).some((category) => jobFunction.includes(category));
    }));
  }

  return selected.slice(0, INTERIM_JOB_LIMIT).map((job, index) => postedJobToCard(job, index + 1, language));
}

export function formatJobMeta(job, language = 'en') {
  return [job.companyLocation, job.workspace, job.schedule].filter(Boolean).join(' · ') || (language === 'ar' ? 'التفاصيل قريباً' : 'Details coming soon');
}

export function formatPostedAge(value, now = Date.now(), language = 'en') {
  const timestamp = parseTimestamp(value);
  if (!timestamp) return '';
  const elapsedDays = Math.max(0, Math.floor((now - timestamp) / (24 * 60 * 60 * 1000)));
  if (elapsedDays === 0) return language === 'ar' ? 'نُشرت اليوم' : 'Posted today';
  if (elapsedDays === 1) return language === 'ar' ? 'نُشرت أمس' : 'Posted yesterday';
  return language === 'ar' ? `نُشرت منذ ${new Intl.NumberFormat('ar-LB').format(elapsedDays)} أيام` : `Posted ${elapsedDays} days ago`;
}

export function matchQualityLabel(score, language = 'en') {
  if (score == null || score === '' || !Number.isFinite(Number(score))) return language === 'ar' ? 'الملاءمة قيد التقييم' : 'Match pending';
  if (Number(score) >= 85) return language === 'ar' ? 'ملاءمة ممتازة' : 'Excellent match';
  if (Number(score) >= 75) return language === 'ar' ? 'ملاءمة قوية' : 'Strong match';
  if (Number(score) >= 60) return language === 'ar' ? 'ملاءمة جيدة' : 'Good match';
  return language === 'ar' ? 'ملاءمة محتملة' : 'Potential match';
}

export function matchQualityTone(score) {
  if (score == null || score === '' || !Number.isFinite(Number(score))) return 'pending';
  if (Number(score) >= 85) return 'excellent';
  if (Number(score) >= 75) return 'strong';
  if (Number(score) >= 60) return 'good';
  return 'potential';
}

export function normalizeFeedEntries(scoutFeed) {
  return Object.entries(scoutFeed || {})
    .map(([jobId, item]) => {
      if (!item || typeof item !== 'object') return null;
      return {
        ...item,
        jobId: item.jobId || jobId,
      };
    })
    .filter((item) => item?.jobId);
}

export function normalizeFeedPayload(feeds, primaryScoutId, language = 'en') {
  const primaryFeed = primaryScoutId ? feeds?.[primaryScoutId] : null;
  // A different Scout represents a different search. Never use its feed as a
  // fallback for the active Scout, even if that other feed has results.
  const sourceFeeds = primaryScoutId ? [[primaryScoutId, primaryFeed]] : [];

  const seen = new Set();
  return sourceFeeds
    .flatMap(([sourceScoutId, scoutFeed]) => normalizeFeedEntries(scoutFeed)
      .map((item) => feedItemToCard(item, sourceScoutId, language)))
    .filter((job) => {
      if (!job.id || seen.has(job.id)) return false;
      seen.add(job.id);
      return true;
    })
    .sort((a, b) => {
      if (a.rank !== b.rank) return a.rank - b.rank;
      return (b.fitScore || 0) - (a.fitScore || 0);
    });
}

export function normalizeAppliedJobs(applications) {
  return new Set(Object.entries(applications || {})
    .filter(([, application]) => (
      application?.status === 'submitted'
      || Boolean(application?.submittedAt)
      || Boolean(application?.appliedAt)
    ))
    .map(([jobId, application]) => application?.jobId || jobId));
}

export function normalizeDismissedJobs(feedback) {
  return new Set(Object.entries(feedback || {})
    .filter(([, item]) => ['dismiss', 'dismissed'].includes(String(item?.action || '').toLowerCase()))
    .map(([jobId, item]) => item?.jobId || jobId));
}

export function normalizeRadarItems(items) {
  return Object.entries(items || {})
    .map(([id, item]) => {
      const value = item || {};
      return {
        id,
        ...value,
        companyName: value.companyName || value.company || value.name || value.companyDomain || 'Company',
      };
    })
    .filter((item) => item.id)
    .sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0));
}

export function isRadarReadyMove(record) {
  if (record?.status !== 'ready') return false;
  const fitScore = Number(record.fitScore || record.fit?.fitScore || record.bestJobMatch?.matchPercent || 0);
  const blockers = Array.isArray(record.blockers) ? record.blockers : record.fit?.blockers || [];
  const hasHighBlocker = blockers.some((blocker) => String(blocker?.severity || '').toLowerCase() === 'high');
  return fitScore >= 75 && !hasHighBlocker;
}

export function sortJobsForMode(baseJobs, sortMode) {
  return [...baseJobs].sort((left, right) => {
    if (sortMode === 'recent') {
      const recency = parseJobTimestamp(right) - parseJobTimestamp(left);
      if (recency !== 0) return recency;
    }

    const rankDifference = Number(left.rank || 999) - Number(right.rank || 999);
    if (rankDifference !== 0) return rankDifference;
    return Number(right.fitScore || 0) - Number(left.fitScore || 0);
  });
}

export function filterJobsForTab(jobs, appliedJobIds, tab) {
  return jobs.filter((job) => {
    const applied = appliedJobIds.has(job.id);
    if (tab === 'applied') return applied;
    if (applied) return false;
    return true;
  });
}

export function processingStateFromValue(value = {}) {
  // Review recovery is not a failed search, and an unknown qualification is
  // not a broken response. Keep those states distinct for the user as well.
  if (value.lastError === 'qualification_retrying') return 'reviewPending';
  if (value.lastError === 'qualification_review_unavailable') return 'reviewUnavailable';
  if (value.lastError === 'qualification_unresolved') return 'reviewUncertain';
  if (value.inventoryStatus === 'incomplete' || value.inventoryStatus === 'quota_deferred') return 'incomplete';
  if (value.inventoryStatus === 'failed' || value.matchStatus === 'error') return 'failed';
  if (value.needsEmbedding === true) return 'embedding';
  // A previous empty-inventory warning can remain while a fresh search runs.
  // Show the current activity instead of presenting that old warning as final.
  if (value.inventoryStatus === 'refreshing'
    || ['queued', 'running', 'retrying'].includes(value.matchStatus)) return 'searching';
  if (value.lastError || ((value.lastResultCount || 0) === 0 && value.lastMatchedAt)) return 'empty';
  if ((value.lastResultCount || 0) === 0) return 'searching';
  return null;
}

